/**
 * rowActions.js — one definition of what a board row's ⋯ offers, rendered two ways:
 * the desktop popover (RowMenu) and the touch bottom sheet (RowSheet).
 */
import { useLang } from '../../i18n';
import { verbId } from '../../logic/lists';
import { itemStatus, isFlagged, isUrgent } from '../../logic/items';
import { STATUS, STATUS_META, STATUS_ORDER } from '../../config/lists';

// Buy-lists refill; task lists (prep / cooking / cleaning / issues) use their own verb,
// custom task lists a plain "Done".
export const doneVerb = (t, list) => (list.sourceable ? t('status.refilled') : t(`act.done.${verbId(list.id)}`));
export const markVerb = (t, list) => (list.sourceable ? t('act.mark.refilled') : t(`act.mark.${verbId(list.id)}`));

/** A row's status facts, shared by both layouts. */
export function rowState(item) {
  const status = itemStatus(item);
  return { status, refilled: status === STATUS.REFILLED, flagged: isFlagged(item), urgent: isUrgent(item), meta: STATUS_META[status] };
}

/**
 * The row's actions (built only while a menu is open):
 *   statuses   buy-lists: refilled / not found / out of stock / needed (empty for task lists)
 *   done       task lists: mark done ⇄ undo
 *   urgent     ⭐ toggle
 *   moves      every other list on the board
 *   fixTelugu  override the auto-translated Telugu name (Telugu view only, else null)
 *   remove     delete (undo-able)
 */
export function useRowActions({ item, list, actions, moveTargets }) {
  const { t, ln, lang, setTranslation } = useLang();
  const { status, refilled, urgent } = rowState(item);
  return {
    statuses: list.sourceable ? STATUS_ORDER.map(s => ({
      key: s, icon: STATUS_META[s].icon, color: STATUS_META[s].color, label: t(`status.${s}`),
      on: status === s, run: () => actions.setStatus(item, s),
    })) : [],
    done: {
      on: refilled, label: refilled ? doneVerb(t, list) : markVerb(t, list),
      run: () => actions.setDone(item, !item.done),
    },
    urgent: {
      on: urgent, icon: urgent ? '☆' : '⭐', label: urgent ? t('menu.removeUrgent') : t('menu.markUrgent'),
      run: () => actions.setUrgent(item, !urgent),
    },
    moves: moveTargets.filter(m => m.id !== list.id).map(m => ({ ...m, run: () => actions.move(item, m.id) })),
    fixTelugu: lang === 'te' ? () => {
      const next = window.prompt(t('menu.fixTeluguPrompt', { name: item.name }), ln(item.name));
      if (next != null && next.trim()) setTranslation(item.name, next.trim());
    } : null,
    remove: () => actions.remove(item),
  };
}
